import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, X } from 'lucide-react';
import PageLoader from '../components/PageLoader';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import type { Reward } from '@crewcast/shared';
import '../styles/rewards.css';

type EditableRewardFields = Pick<
  Reward,
  'title' | 'description' | 'points' | 'isActive'
>;

export default function RewardDetailPage() {
  const { rewardId } = useParams<{ rewardId: string }>();
  const [reward, setReward] = useState<Reward | null>(null);
  const [form, setForm] = useState<EditableRewardFields | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claimModalError, setClaimModalError] = useState('');

  const navigate = useNavigate();
  const { token, user: currentUser, setUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const isEmployee = currentUser?.role === 'employee';
  const currentPoints = currentUser?.points ?? 0;
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (rewardId) {
      fetchReward(rewardId);
    } else {
      setError('Missing reward id');
      setLoading(false);
    }
  }, [rewardId]);

  const fetchReward = async (id: string) => {
    setLoading(true);
    setError('');

    try {
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/rewards/${id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch reward');
      }

      setReward(data.reward);
      setForm({
        title: data.reward.title,
        description: data.reward.description,
        points: data.reward.points,
        isActive: data.reward.isActive,
      });
      setImagePreview(data.reward.image || '');
    } catch (err: any) {
      setError(err.message || 'Failed to fetch reward');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rewardId || !form) return;

    setSaveError('');
    setSaving(true);

    try {
      if (!token) throw new Error('Not authenticated');

      let image: string | undefined;

      // Upload image if selected
      if (imageFile) {
        setUploadingImage(true);
        const presignedResponse = await fetch(
          `${authConfig.apiUrl}/api/upload/presigned-url`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              fileName: imageFile.name,
              fileType: imageFile.type,
            }),
          }
        );

        if (!presignedResponse.ok) {
          throw new Error('Failed to get upload URL');
        }

        const { uploadUrl, publicUrl } = await presignedResponse.json();

        // Upload to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': imageFile.type,
          },
          body: imageFile,
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload image');
        }

        image = publicUrl;
        setUploadingImage(false);
      }

      const payload = {
        title: form.title.trim(),
        description: form.description?.trim() || undefined,
        points: Number(form.points) || 0,
        isActive: form.isActive,
        image: image || reward?.image || undefined,
      };

      if (!payload.title) {
        throw new Error('Title is required');
      }

      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/rewards/${rewardId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update reward');
      }

      // Re-fetch to reflect latest data
      await fetchReward(rewardId);
      setImageFile(null);
      
      // Redirect back to rewards page after successful save
      navigate('/rewards');
    } catch (err: any) {
      setSaveError(err.message || 'Failed to update reward');
    } finally {
      setSaving(false);
      setUploadingImage(false);
    }
  };

  const handleClaim = async () => {
    if (!rewardId) return;
    try {
      if (!token) throw new Error('Not authenticated');

      setClaiming(true);
      setClaimModalError('');

      const response = await fetch(`${authConfig.apiUrl}/api/rewards/${rewardId}/claim`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to claim reward');
      }

      if (currentUser && typeof data.remainingPoints === 'number') {
        setUser({ ...currentUser, points: data.remainingPoints });
      }

      setClaimModalOpen(true);
    } catch (err: any) {
      setClaimModalError(err.message || 'Failed to claim reward');
      setClaimModalOpen(true);
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="rewards-container">
        <PageLoader />
      </div>
    );
  }

  if (error || !reward || !form) {
    return (
      <div className="rewards-container">
        <div className="rewards-error">{error || 'Reward not found'}</div>
        <button
          className="rewards-secondary-btn"
          style={{ marginTop: '16px' }}
          onClick={() => navigate('/rewards')}
        >
          Back to Rewards
        </button>
      </div>
    );
  }

  return (
    <div className="rewards-container">
      <div className="rewards-header">
        <div>
          <h1 className="rewards-title">{reward.title}</h1>
          <p className="rewards-subtitle">
            Reward details · {reward.points} points
          </p>
        </div>

        <button
          className="rewards-secondary-btn"
          onClick={() => navigate('/rewards')}
        >
          Back to Rewards
        </button>
      </div>

      <div className="rewards-card rewards-detail-card">
        {isAdmin ? (
          <form onSubmit={handleSave} className="rewards-form">
            <div className="rewards-form-row">
              <label className="rewards-label">
                Title
                <input
                  type="text"
                  className="rewards-input"
                  value={form.title}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev
                        ? {
                            ...prev,
                            title: e.target.value,
                          }
                        : prev
                    )
                  }
                  required
                />
              </label>
            </div>

            <div className="rewards-form-row">
              <label className="rewards-label">
                Description
                <textarea
                  className="rewards-textarea"
                  value={form.description || ''}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev
                        ? {
                            ...prev,
                            description: e.target.value,
                          }
                        : prev
                    )
                  }
                  rows={4}
                />
              </label>
            </div>

            <div className="rewards-form-row">
              <label className="rewards-label">Points</label>
              <input
                type="number"
                min={0}
                className="rewards-input"
                value={form.points}
                onChange={(e) =>
                  setForm((prev) =>
                    prev
                      ? {
                          ...prev,
                          points: Number(e.target.value),
                        }
                      : prev
                  )
                }
                required
              />
            </div>

            <div className="rewards-form-row">
              <label className="rewards-label">Status</label>
              <div className="rewards-radio-group">
                <label className="rewards-radio-label">
                  <input
                    type="radio"
                    name="status"
                    checked={!form.isActive}
                    onChange={() =>
                      setForm((prev) =>
                        prev
                          ? {
                              ...prev,
                              isActive: false,
                            }
                          : prev
                      )
                    }
                  />
                  <span>Inactive</span>
                </label>
                <label className="rewards-radio-label">
                  <input
                    type="radio"
                    name="status"
                    checked={form.isActive}
                    onChange={() =>
                      setForm((prev) =>
                        prev
                          ? {
                              ...prev,
                              isActive: true,
                            }
                          : prev
                      )
                    }
                  />
                  <span>Active</span>
                </label>
              </div>
            </div>

            <div className="rewards-form-row">
              <label className="rewards-label">
                Reward Image
                <input
                  type="file"
                  accept="image/*"
                  className="rewards-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setImageFile(file);
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setImagePreview(reader.result as string);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </label>
              {imagePreview && (
                <div className="rewards-image-preview">
                  <img src={imagePreview} alt="Reward" />
                </div>
              )}
            </div>

            {saveError && (
              <div className="rewards-error-inline">{saveError}</div>
            )}

            <div className="rewards-form-actions">
              <button
                type="submit"
                className="rewards-primary-btn"
                disabled={saving || uploadingImage}
              >
                {uploadingImage ? 'Uploading...' : saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        ) : (
          <div className="rewards-detail-readonly">
            {reward.image && (
              <div className="rewards-detail-image">
                <img src={reward.image} alt={reward.title} />
              </div>
            )}
            <h2 className="rewards-card-title">{reward.title}</h2>
            {reward.description && (
              <p className="rewards-card-description">{reward.description}</p>
            )}
            <div className="rewards-card-meta">
              <span className="rewards-points">{reward.points} pts</span>
              <span
                className={`rewards-status-badge ${
                  reward.isActive ? 'active' : 'inactive'
                }`}
              >
                {reward.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            {isEmployee && reward.isActive && (
              <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
                <button
                  className="rewards-primary-btn"
                  disabled={claiming || (reward.points ?? 0) > currentPoints}
                  onClick={handleClaim}
                >
                  {claiming
                    ? 'Claiming...'
                    : (reward.points ?? 0) > currentPoints
                    ? 'Not enough points'
                    : 'Claim Reward'}
                </button>
                <button
                  className="rewards-secondary-btn"
                  onClick={() => navigate('/claimed-rewards')}
                >
                  View My Claims
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {claimModalOpen && reward && (
        <div
          className="crewcast-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="claim-reward-detail-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setClaimModalOpen(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setClaimModalOpen(false);
          }}
          tabIndex={-1}
        >
          <div className="crewcast-modal-card">
            <div className="crewcast-modal-header">
              <div>
                <h2 id="claim-reward-detail-title" className="crewcast-modal-title">
                  {claimModalError ? 'Couldn’t claim reward' : 'Reward claimed!'}
                </h2>
                <div className="crewcast-modal-subtitle">
                  {reward.title} • {reward.points} pts
                </div>
              </div>
              <button
                type="button"
                className="crewcast-modal-close"
                onClick={() => setClaimModalOpen(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="crewcast-modal-body">
              {claimModalError ? (
                <>
                  <div className="crewcast-modal-hint danger">
                    <AlertCircle size={16} />
                    <span>Something went wrong while claiming this reward.</span>
                  </div>
                  <div className="crewcast-modal-error">{claimModalError}</div>
                </>
              ) : (
                <div className="crewcast-modal-hint">
                  <CheckCircle2 size={16} />
                  <span>Your claim has been submitted. You can view it in “My Claimed Rewards”.</span>
                </div>
              )}
            </div>

            <div className="crewcast-modal-actions">
              <button
                type="button"
                className="crewcast-modal-btn secondary"
                onClick={() => setClaimModalOpen(false)}
              >
                Close
              </button>
              {!claimModalError && (
                <button
                  type="button"
                  className="crewcast-modal-btn primary"
                  onClick={() => navigate('/claimed-rewards')}
                >
                  View my claims
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


